import React, { CSSProperties, useState, useEffect } from 'react';
import Select, { StylesConfig } from 'react-select';
import './numberInput.css'; // Import the custom CSS file
import { useAppContext } from '../../apps/AppContext';
import { CurrencyList } from '../../../DS-Files';
import LabelWithTooltip from './CustomLabel';
import { mergeSelectStyles } from './SelectStyles';

type SuffixOption = { value: string; label: string };

// MBAI-436: compact, search-enabled dropdown rendered inside the input's suffix box
const suffixSelectStyles: StylesConfig<SuffixOption, false> = mergeSelectStyles({
  container: (base) => ({ ...base, marginLeft: '-1px', flex: '0 0 auto' }),
  control: (base, state) => ({
    ...base,
    minHeight: 'unset',
    height: '100%',
    minWidth: '76px',
    maxWidth: '120px',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: 'var(--bs-border-radius)',
    borderBottomRightRadius: 'var(--bs-border-radius)',
    borderColor: state.isFocused ? 'var(--bs-gray-400)' : 'var(--bs-gray-300)',
    backgroundColor: 'var(--bs-gray-100)', // matches native .input-group-text (see suffix span below, DiscountInput's CustomToggle)
    boxShadow: 'none',
    cursor: 'pointer',
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px', fontSize: '0.8rem', flexWrap: 'nowrap' }),
  indicatorsContainer: (base) => ({ ...base, padding: '0 4px' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 2px' }),
  placeholder: (base) => ({ ...base, color: 'var(--bs-gray-500)' }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: 'var(--bs-gray-700)' }),
  singleValue: (base) => ({ ...base, overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--bs-gray-700)' }),
  menu: (base) => ({ ...base, minWidth: '180px', fontSize: '0.85rem', backgroundColor: 'var(--bs-dropdown-bg)' }),
  option: (base, state) => ({
    ...base,
    color: state.isSelected || state.isFocused ? base.color : 'var(--bs-dropdown-link-color)', // matches native .dropdown-item
  }),
});


interface NumberInputProps {
  label?: string;
  name: string;
  value?: number;
  placeholder?: string;
  showCurrency?: boolean;
  currId?: string;
  suffix?: string;
  suffixOptions?: { value: string; label: string }[];
  suffixValue?: string;
  onSuffixChange?: (value: string) => void;
  style?: CSSProperties | undefined;
  inputDisabled?: boolean;
  tooltip?: string;
  precision?: number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, parsedValue: number | undefined) => void;
  error?: string; // New error prop
}

const NumberInput: React.FC<NumberInputProps> = ({
  label,
  name,
  value,
  placeholder,
  showCurrency,
  currId,
  suffix,
  suffixOptions,
  suffixValue,
  onSuffixChange,
  style,
  inputDisabled,
  tooltip,
  onChange,
  precision,
  error, // Destructure the error prop
}) => {
  const { bizMaster } = useAppContext();
  const [displayValue, setDisplayValue] = useState<string>('');

  // Update display value when external value changes
  // In the useEffect, update this section:
  useEffect(() => {
    if (value === null || value === undefined  || isNaN(value)) {
      setDisplayValue('');
    } else {
      const numValue = Number(value);
      const decimalPart = value.toString().split('.')[1];

      // Apply toFixed only if precision > 4
      const restrictedValue =
        decimalPart && decimalPart.length > 4
          ? numValue.toFixed(4)
          : value.toString();

      setDisplayValue(restrictedValue);
    }
  }, [value]);


  const currSymbol = Boolean(currId)
    ? CurrencyList.find(
      (currency) => currency.currencyID === currId
    )?.symbol
    : CurrencyList.find(
      (currency) => currency.currencyID === bizMaster?.bizDetails?.currency
    )?.symbol;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Update display value immediately for user feedback
    setDisplayValue(inputValue);

    // Allow empty string
    if (inputValue === '') {
      onChange(e, undefined);
      return;
    }

    // Allow "-" or "-." temporarily for negative number input
    if (inputValue === '-' || inputValue === '-.') {
      // Don't call onChange with a parsed value, just keep the display
      // The parent component won't receive a value update, but user sees the "-"
      return;
    }

    // Parse the value
    const parsedValue = parseFloat(inputValue);

    // Check if it's a valid number (including negative numbers)
    if (!isNaN(parsedValue) && isFinite(parsedValue)) {
      onChange(e, parsedValue);
    } else {
      // For invalid input, revert display value to previous valid state
      if (value !== undefined && value !== null) {
        setDisplayValue(value.toString());
      } else {
        setDisplayValue('');
      }
    }
  };

  return (
    <div className={label ? '' : ''}>
      {label && <LabelWithTooltip labelText={label} tooltipText={tooltip}></LabelWithTooltip>}
      <div className="d-flex flex-column">
        <div className="input-group flex-nowrap" style={{ minWidth: '60px' }}>
          {showCurrency && (
            <span className="rounded-0 rounded-start input-group-text">
              {currSymbol || '$'}
            </span>
          )}
          <input
            type="text" // 👈 use text instead of number to avoid auto-validation issues
            inputMode="decimal" // 👈 still brings up numeric keyboard on mobile
            className={`form-control no-spinner text-end ${error ? 'is-invalid' : ''}`}
            placeholder={placeholder || label}
            name={name}
            value={displayValue}
            style={style}
            disabled={inputDisabled}
            onChange={handleInputChange}
          />

          {/* MBAI-436: suffix box doubles as a searchable UOM picker when suffixOptions is supplied */}
          {suffixOptions && suffixOptions.length > 0 ? (
            <Select<SuffixOption, false>
              classNamePrefix="suffix-select"
              options={suffixOptions}
              value={suffixOptions.find(o => o.value === suffixValue) || null}
              onChange={(option) => option && onSuffixChange?.(option.value)}
              placeholder={'UOM'.toTranslatedDisplay()}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={suffixSelectStyles}
            />
          ) : suffix && (
            <span
              className="rounded-0 rounded-end input-group-text"
              title={suffix}
              style={{
                maxWidth: '72px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                cursor: 'default',
                fontSize: '0.8rem',
              }}
            >
              {suffix}
            </span>
          )}

        </div>
        {error && <div className="invalid-feedback d-block text-end">{error}</div>}
      </div>
    </div>
  );
};

export default NumberInput;